import { Button, Select, FontAwesomeIcon } from "@inceptionbg/ui-components";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../../../AppContent";
import { Checkbox } from "../../../../Elements/Checkbox";
import { Loader } from "../../../../Elements/Loader";
import { SubSection } from "../../../SubSection";
import {
  CommunicationChannels,
  ICommunicationChannel,
  IPrimaryCommunicationChannel,
} from "../../../../types/IFarm";
import {
  updateCommunicationChannel,
  farmHasEDelivery,
} from "../../../../repos/FarmRepo";
import { ISelectData } from "@inceptionbg/ui-components";
import { faTriangleExclamation } from "../../../../assets/icons/solid/faTriangleExclamation";

export const CommunicationChannel: FC = () => {
  const { t } = useTranslation();
  const { farm, reloadFarm } = useContext(FarmContext);
  const [formData, setFormData] = useState<ICommunicationChannel>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasEDelivery, setHasEDelivery] = useState(false);

  const holderType = farm?.holder.type;
  const sectionNumber = holderType === "physicalPerson" ? "1.5." : "1.6.";

  useEffect(() => {
    if (farm?.uuid) {
      farmHasEDelivery(farm.uuid)
        .then((hasDelivery) => {
          setHasEDelivery(hasDelivery);
        })
        .catch(() => {});
    }
  }, [farm?.uuid]);

  useEffect(() => {
    if (farm?.communicationChannel) {
      setFormData({
        eInboxDelivery: farm.communicationChannel.eInboxDelivery ?? false,
        eInboxInfo: farm.communicationChannel.eInboxInfo ?? false,
        emailDelivery: farm.communicationChannel.emailDelivery ?? false,
        emailInfo: farm.communicationChannel.emailInfo ?? false,
        smsDelivery: farm.communicationChannel.smsDelivery ?? false,
        smsInfo: farm.communicationChannel.smsInfo ?? false,
        primaryCommunicationChannel:
          farm.communicationChannel.primaryCommunicationChannel ?? "SMS",
      });
    } else {
      setFormData({
        eInboxDelivery: false,
        eInboxInfo: false,
        emailDelivery: false,
        emailInfo: false,
        smsDelivery: false,
        smsInfo: false,
        primaryCommunicationChannel: "SMS",
      });
    }
  }, [farm?.communicationChannel]);

  const emailVerified = farm?.contactInfo?.emailVerified ?? false;
  const phoneNumberVerified = farm?.contactInfo?.phoneNumberVerified ?? false;

  const emailDisabled = !emailVerified;
  const smsViberDisabled = !phoneNumberVerified;
  const eInboxDisabled = !hasEDelivery;

  const onSubmit = () => {
    if (farm?.uuid) {
      setIsLoading(true);
      updateCommunicationChannel(farm.uuid, {
        eInboxDelivery: formData.eInboxDelivery ?? false,
        eInboxInfo: formData.eInboxInfo ?? false,
        emailDelivery: formData.emailDelivery ?? false,
        emailInfo: formData.emailInfo ?? false,
        smsDelivery: formData.smsDelivery ?? false,
        smsInfo: formData.smsInfo ?? false,
        primaryCommunicationChannel:
          (formData.primaryCommunicationChannel as string) || "SMS",
      })
        .then(() => {
          reloadFarm(() => {
            setIsLoading(false);
          });
        })
        .catch(() => setIsLoading(false));
    }
  };

  const onCancel = () => {
    if (farm?.communicationChannel) {
      setFormData({
        eInboxDelivery: farm.communicationChannel.eInboxDelivery ?? false,
        eInboxInfo: farm.communicationChannel.eInboxInfo ?? false,
        emailDelivery: farm.communicationChannel.emailDelivery ?? false,
        emailInfo: farm.communicationChannel.emailInfo ?? false,
        smsDelivery: farm.communicationChannel.smsDelivery ?? false,
        smsInfo: farm.communicationChannel.smsInfo ?? false,
        primaryCommunicationChannel:
          farm.communicationChannel.primaryCommunicationChannel ?? "SMS",
      });
    }
  };

  return (
    <SubSection
      title={`${sectionNumber} ${t("CommunicationChannelTitle")}`}
      expandable
    >
      <Loader isLoading={isLoading}>
        <div className="form-container">
          <div className="flex align-start mb-4" style={{ gap: "24px" }}>
            <div style={{ width: "300px", flexShrink: 0 }}>
              <p className="label-text">{t("CommunicationChannelEInbox")}</p>
              <p
                className="text-sm label-desc-text"
                style={{ wordWrap: "break-word", lineHeight: "1.4" }}
              >
                {t("CommunicationChannelEInboxSubtitle")}
              </p>
            </div>
            <div className="flex column" style={{ gap: "12px", flex: 1 }}>
              <Checkbox
                label={t("CommunicationChannelDocumentDelivery")}
                selected={formData.eInboxDelivery ?? false}
                setSelected={() => {}}
                disabled={true}
              />
              <Checkbox
                label={t("CommunicationChannelNotifications")}
                selected={formData.eInboxInfo ?? false}
                setSelected={(checked) =>
                  setFormData({ ...formData, eInboxInfo: checked })
                }
                disabled={eInboxDisabled}
              />
            </div>
          </div>

          <div className="flex align-start mb-4" style={{ gap: "24px" }}>
            <div style={{ width: "300px", flexShrink: 0 }}>
              <p className="label-text">{t("CommunicationChannelEmail")}</p>
              <p
                className="text-sm label-desc-text"
                style={{ wordWrap: "break-word", lineHeight: "1.4" }}
              >
                {t("CommunicationChannelEmailSubtitle")}
              </p>
            </div>
            <div className="flex column" style={{ gap: "12px", flex: 1 }}>
              <div className="flex align-center" style={{ gap: "8px" }}>
                <Checkbox
                  label={t("CommunicationChannelDocumentDelivery")}
                  selected={formData.emailDelivery ?? false}
                  setSelected={(checked) =>
                    setFormData({ ...formData, emailDelivery: checked })
                  }
                  disabled={emailDisabled}
                />
                {emailDisabled && (
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="color-error"
                  />
                )}
              </div>
              <Checkbox
                label={t("CommunicationChannelNotifications")}
                selected={formData.emailInfo ?? false}
                setSelected={(checked) =>
                  setFormData({ ...formData, emailInfo: checked })
                }
                disabled={emailDisabled}
              />
              {emailDisabled && (
                <p className="error-text">
                  {t("CommunicationChannelEmailNotVerified")}
                </p>
              )}
            </div>
          </div>

          <div className="flex align-start mb-4" style={{ gap: "24px" }}>
            <div style={{ width: "300px", flexShrink: 0 }}>
              <p className="label-text">{t("CommunicationChannelSMSViber")}</p>
              <p
                className="text-sm label-desc-text"
                style={{ wordWrap: "break-word", lineHeight: "1.4" }}
              >
                {t("CommunicationChannelSMSViberSubtitle")}
              </p>
            </div>
            <div className="flex column" style={{ gap: "12px", flex: 1 }}>
              <div className="flex align-center" style={{ gap: "8px" }}>
                <Checkbox
                  label={t("CommunicationChannelDocumentDelivery")}
                  selected={formData.smsDelivery ?? false}
                  setSelected={(checked) =>
                    setFormData({ ...formData, smsDelivery: checked })
                  }
                  disabled={smsViberDisabled}
                />
                {smsViberDisabled && (
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="color-error"
                  />
                )}
              </div>
              <Checkbox
                label={t("CommunicationChannelNotifications")}
                selected={formData.smsInfo ?? false}
                setSelected={(checked) =>
                  setFormData({ ...formData, smsInfo: checked })
                }
                disabled={smsViberDisabled}
              />
              {smsViberDisabled && (
                <p className="error-text">
                  {t("CommunicationChannelNumberNotVerified")}
                </p>
              )}
            </div>
          </div>

          <div className="flex align-start mb-4" style={{ gap: "24px" }}>
            <div style={{ width: "300px", flexShrink: 0 }}>
              <p className="label-text">
                {t("CommunicationChannelPrimaryTitle")}
              </p>
              <p
                className="text-sm label-desc-text"
                style={{ wordWrap: "break-word", lineHeight: "1.4" }}
              >
                {t("CommunicationChannelPrimarySubtitle")}
              </p>
            </div>
            <Select
              value={
                formData.primaryCommunicationChannel
                  ? {
                      label: t(
                        `CommunicationChannel${formData.primaryCommunicationChannel}`
                      ),
                      value: formData.primaryCommunicationChannel,
                    }
                  : null
              }
              options={CommunicationChannels.map((e) => ({
                label: t("CommunicationChannel" + e),
                value: e,
              }))}
              onChange={(option: ISelectData | null) =>
                setFormData({
                  ...formData,
                  primaryCommunicationChannel:
                    (option?.value as IPrimaryCommunicationChannel) || "SMS",
                })
              }
              disabled={false}
              placeholder={t("CommunicationChannelSelectPrimary")}
            />
          </div>

          <div className="flex justify-right gap-2">
            <Button
              label={t("Cancel")}
              outlined
              onClick={onCancel}
              disabled={isLoading}
              size="s"
            />
            <Button
              label={t("CommunicationChannelSaveOptions")}
              primary
              onClick={onSubmit}
              disabled={isLoading}
              size="s"
            />
          </div>
        </div>
      </Loader>
    </SubSection>
  );
};
